import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown, Database, GraduationCap, LibraryBig, LogOut, Menu, Newspaper, PersonStanding, User, X } from 'lucide-react';
import { Themes, useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import logo from "@/assets/images/iasma.png";
import logo_white from "@/assets/images/iasma-white.png";
import { getExpiredInfo } from '../../../constants';
import { NavItem } from '@/components/layout/Menu/MenuGridIcon';
import { Roles } from 'iasma-datatypes';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { theme, nextTheme, isDarkMode } = useTheme();
  const { session, signout } = useAuth();
  const [debugOutline, setDebugOutline] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navItems: NavItem[] = [
    { label: 'Basisdata', to: '/directory', icon: <GraduationCap size={20} /> },
    {
      label: 'Komunitas', to: '/community', icon: <PersonStanding size={20} />,
      roles: [Roles.SUPER_SUPER]
    },
  ];

  useEffect(() => {
    if (debugOutline) {
      document.documentElement.classList.add('debug-outline');
    } else {
      document.documentElement.classList.remove('debug-outline');
    }
  }, [debugOutline]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // console.log('Current theme in Header:', theme);
  return (
    <header className='sticky top-0 z-100 backdrop-blur-md bg-(--color-surface)/50 text-(--color-primary) border-b border-gray-200'>
      <nav className="transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 md:h-16 py-2 md:py-0">
            {/* Left: Logo */}
            <div className="flex items-center justify-between">
              <NavLink to="/">
                <img src={isDarkMode ? logo_white : logo} alt="" className="w-10 h-10 mr-2" />
              </NavLink>
              <span className="text-xl font-bold tracking-tight">
                <NavLink to="/">
                  IASMA
                </NavLink>
              </span>
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>

            {/* Right: Navigation Menu */}
            <div className={`flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 w-full md:w-auto ${isMenuOpen ? 'flex' : 'hidden md:flex'}`}>
              {/* {session?.User?.Alumni && navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ease-in-out hover:bg-(--color-surface-hover) text-(--color-text) hover:text-(--color-text-hover)"
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))} */}
              <div className="relative w-full md:w-auto" ref={dropdownRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ease-in-out hover:bg-(--color-surface-hover) text-(--color-text) hover:text-(--color-text-hover)"
                >
                  {session?.User?.Avatar
                    ? <img src={session.User.Avatar} alt="👤" className="w-5 h-5 rounded-full object-cover" />
                    : <User size={20} />}

                  <span className={`font-medium ${isMenuOpen ? 'flex' : 'hidden lg:flex'}`} >{session?.User?.Alumni?.Name}</span>
                  <ChevronDown size={16} className={`transform transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {isProfileOpen && (
                  <div className="absolute left-0 md:right-0 md:left-auto mt-2 w-64 rounded-lg shadow-lg py-2 z-50 border bg-(--color-surface) border-(--color-border)">
                    <div className="px-4 py-3 border-(--color-border)">
                      <div>
                        <div className='p-1'>
                          {session?.User?.Avatar &&
                            <img src={session.User.Avatar} alt="👤" className="w-12 h-12 rounded-full object-cover" onClick={() => setDebugOutline(!debugOutline)} />
                          }
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-(--color-text)" >{session?.User?.Alumni?.Name}</p>
                          <p className="text-sm text-(--color-text-secondary)">{session?.User?.Email}</p>
                          <p className="font-bold text-xs text-(--color-text-secondary)">{session?.User?.Alumni?.Graduation}</p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={nextTheme}
                      className="w-full px-4 py-2 text-left flex items-center space-x-3 hover:bg-(--color-surface-hover) transition-colors"
                    >
                      {Themes[theme].icon}
                      <span className="text-sm text-(--color-text) hover:text-(--color-text-secondary) transition-colors">
                        {Themes[theme].name}
                      </span>
                    </button>
                    <div className="w-full px-4 py-2 text-left ">
                      <button onClick={signout} className="flex items-center space-x-3 hover:bg-(--color-surface-hover) transition-colors text-(--color-text) hover:text-(--color-text-secondary) ">
                        <LogOut size={18} />
                        <span className="text-sm font-medium">Log Out</span>
                      </button>
                      <span className="text-xs text-(--color-text-secondary)">Session {getExpiredInfo(session?.ExpiredAt!.toLocaleString())}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};